import React from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';

// icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// ----------------------------------------------------------------------

const TimeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'compact',
})(({theme, isSelected, compact}) => ({
  width: '100%',
  height: compact ? 32 : 36,
  borderRadius: theme.shape.borderRadius,
  fontSize: compact ? '0.75rem' : '0.8rem',
  transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
    duration: theme.transitions.duration.standard,
  }),
  ...(isSelected && {
    'backgroundColor': theme.palette.primary.main,
    'color': theme.palette.primary.contrastText,
    'boxShadow': `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
    'transform': 'translateY(-2px)',
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'translateY(-2px)',
    },
  }),
  ...(!isSelected && {
    'borderColor': theme.palette.divider,
    '&:hover, &:focus': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderColor: theme.palette.primary.main,
      transform: 'translateY(-1px)',
      boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  }),
}));

// ----------------------------------------------------------------------

export function TimeSlotPicker({
  timeSlots,
  selectedTime,
  onTimeSelect,
  compact = false,
  showGroupHeaders = false, // Par défaut, ne pas montrer les en-têtes de groupe
}) {
  // S'assurer que timeSlots est un tableau
  const safeTimeSlots = (() => {
    if (!timeSlots) return [];
    if (timeSlots.timeslots && Array.isArray(timeSlots.timeslots)) {
      return timeSlots.timeslots;
    }
    if (Array.isArray(timeSlots)) {
      return timeSlots;
    }
    return [];
  })();

  // Trier les créneaux horaires par heure
  const sortedTimeSlots = [...safeTimeSlots].sort((a, b) => {
    if (!a || !a.time || !b || !b.time) return 0;
    return a.time.localeCompare(b.time);
  });

  // Si aucun créneau disponible
  if (sortedTimeSlots.length === 0) {
    return (
      <Alert severity="info" sx={{mb: 2, fontSize: compact ? '0.75rem' : '0.85rem'}}>
        Aucun créneau disponible pour cette date. Veuillez sélectionner une autre date ou contacter directement le restaurant.
      </Alert>
    );
  }

  // Affichage de tous les créneaux sans regroupement
  return (
    <Box sx={{mb: compact ? 1 : 2}}>
      <Grid container spacing={compact ? 0.75 : 1}>
        {sortedTimeSlots.map((slot) => {
          if (!slot || typeof slot.time !== 'string') return null;

          const isSelected = selectedTime === slot.time;

          return (
            <Grid item xs={3} key={slot.time}>
              <TimeChip
                label={slot.time}
                variant={isSelected ? 'filled' : 'outlined'}
                onClick={() => onTimeSelect(slot.time)}
                isSelected={isSelected}
                clickable
                compact={compact}
                icon={compact ? null : <AccessTimeIcon fontSize="small" />}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

TimeSlotPicker.propTypes = {
  timeSlots: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  selectedTime: PropTypes.string,
  onTimeSelect: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  showGroupHeaders: PropTypes.bool,
};
